// No need to import React in React 19
import { useEffect, lazy, Suspense } from 'react';
import { Provider } from 'react-redux';
import { store } from './store';
import { AppLayout } from './components/layout/AppLayout';
import { ThemeProvider } from './providers/ThemeProvider';
import { KeyboardShortcutsProvider } from './providers/KeyboardShortcutsProvider';
import { ToastProvider } from './hooks/useToast';
import { ErrorBoundary } from './components/ErrorBoundary';
import { useOnboarding } from './components/onboarding';
import { useShortcutsHelp } from './components/shortcuts/hooks';
import { Toaster } from 'sonner';

// Lazy load heavy components that are conditionally rendered
const CommandPalette = lazy(() =>
  import('./components/CommandPalette').then((module) => ({
    default: module.CommandPalette,
  }))
);
const SettingsPanel = lazy(() =>
  import('./components/SettingsPanelSimple').then((module) => ({
    default: module.SettingsPanel,
  }))
);
const OnboardingModal = lazy(() =>
  import('./components/onboarding').then((module) => ({
    default: module.OnboardingModal,
  }))
);
const ShortcutsHelpOverlay = lazy(() =>
  import('./components/shortcuts/ShortcutsHelpOverlay').then((module) => ({
    default: module.ShortcutsHelpOverlay,
  }))
);
const GlobalCreateFileDialog = lazy(() =>
  import('./components/GlobalCreateFileDialog').then((module) => ({
    default: module.GlobalCreateFileDialog,
  }))
);
const GlobalSearchModal = lazy(() =>
  import('./components/GlobalSearchModal').then((module) => ({
    default: module.GlobalSearchModal,
  }))
);

function AppContent() {
  const { showOnboarding, isInitializing, setShowOnboarding } = useOnboarding();
  const shortcutsHelp = useShortcutsHelp();

  // Listen for onboarding events
  useEffect(() => {
    const handleOpenOnboarding = (_event: CustomEvent) => {
      setShowOnboarding(true);
      // If a specific step is requested, we could handle it here
      // For now, just open the modal
    };

    window.addEventListener(
      'phrasewright:open-onboarding',
      handleOpenOnboarding as EventListener
    );

    return () => {
      window.removeEventListener(
        'phrasewright:open-onboarding',
        handleOpenOnboarding as EventListener
      );
    };
  }, [setShowOnboarding]);

  // Handle custom keyboard shortcut events
  useEffect(() => {
    const handleShowShortcuts = () => {
      shortcutsHelp.show();
    };

    const handleShowHelp = () => {
      shortcutsHelp.show();
    };

    // Listen for custom shortcut events
    window.addEventListener('phrasewright:show-shortcuts', handleShowShortcuts);
    window.addEventListener('phrasewright:show-help', handleShowHelp);

    return () => {
      window.removeEventListener(
        'phrasewright:show-shortcuts',
        handleShowShortcuts
      );
      window.removeEventListener('phrasewright:show-help', handleShowHelp);
    };
  }, [shortcutsHelp]);

  if (isInitializing) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <>
      <AppLayout onStartOnboarding={() => setShowOnboarding(true)} />
      <Suspense fallback={null}>
        <CommandPalette />
      </Suspense>
      <Suspense fallback={null}>
        <SettingsPanel />
      </Suspense>
      <Suspense fallback={null}>
        <OnboardingModal
          open={showOnboarding}
          onOpenChange={setShowOnboarding}
        />
      </Suspense>
      <Suspense fallback={null}>
        <ShortcutsHelpOverlay
          isOpen={shortcutsHelp.isOpen}
          onClose={shortcutsHelp.hide}
        />
      </Suspense>
      <Suspense fallback={null}>
        <GlobalCreateFileDialog />
      </Suspense>
      <Suspense fallback={null}>
        <GlobalSearchModal />
      </Suspense>
      <Toaster richColors />
    </>
  );
}

export function App() {
  return (
    <Provider store={store}>
      <ThemeProvider>
        <KeyboardShortcutsProvider>
          <ErrorBoundary>
            <ToastProvider>
              <AppContent />
            </ToastProvider>
          </ErrorBoundary>
        </KeyboardShortcutsProvider>
      </ThemeProvider>
    </Provider>
  );
}
